import React, { useEffect, useState } from 'react'
import { useApiFormProviderClaimMapping } from '../../api-new/state/auth'
import { useApiListRoleOrg } from '../../api-new/state/content'
import { Drawer } from '../drawer/drawer'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

const blank = claim => ({ claimName: claim || '', claimValue: '', organizationID: '', roleID: '', label: '' })

// Side panel for adding a single role mapping: this claim (name, value) maps
// to a role in an organization when the identity provider sends it. Renders
// inside the page's FormNew, so it stays plain controlled inputs rather than
// a nested <form> (invalid HTML) or FormNew (would submit the outer form too).
export const RoleMappingPanel = ({ open, claim, providerID, organizations = [], onClose, onAdded }) => {
  const api = useApiFormProviderClaimMapping()
  const roles = useApiListRoleOrg()
  const [form, setForm] = useState(blank(claim))
  const [saving, setSaving] = useState(false)
  const [error, failure, setError] = useErrorLine()

  // Reopening (or opening with a different pre-filled claim) starts clean --
  // including any error message left over from a previous failed attempt.
  useEffect(() => { if (open) { setForm(blank(claim)); setError(null) } }, [open, claim, setError])
  useEffect(() => { if (open && form.organizationID) roles.get() }, [open, form.organizationID]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const set = key => e => setForm({ ...form, [key]: e.target.value })
  // Escape is Cancel: the Drawer handles it, and stops it inside the panel
  // so it does only that (the claim chips' document listener never sees it).
  // Focus starts on the first thing to fill: the claim, or its value when
  // the panel was opened from a claim chip.
  const ready = form.claimName.trim() && form.claimValue.trim() && form.organizationID && form.roleID

  const handleAdd = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.post({
        providerID: Number(providerID),
        organizationID: Number(form.organizationID),
        roleID: Number(form.roleID),
        claimName: form.claimName.trim(),
        claimValue: form.claimValue.trim(),
        label: form.label
      })
      onAdded()
    } catch (exp) {
      setError((exp && exp.data && exp.data.message) || 'Could not add the mapping.', exp)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer open title="Add role mapping" label="add role mapping" onClose={onClose}>
      <p className="small sw-hint m-0">People whose token carries this claim value get the role in that organization when they sign in. Roles are only ever added, never removed.</p>
      <div><label className="sw-label mb-1" htmlFor="rm-claim">Claim</label><input id="rm-claim" className="form-control sw-mono" value={form.claimName} onChange={set('claimName')} autoFocus={!claim} /></div>
      <div><label className="sw-label mb-1" htmlFor="rm-value">Value</label><input id="rm-value" className="form-control sw-mono" value={form.claimValue} onChange={set('claimValue')} autoFocus={Boolean(claim)} placeholder="the exact value the identity provider sends" /></div>
      <div><label className="sw-label mb-1" htmlFor="rm-org">Organization</label>
        <select id="rm-org" className="form-select" value={form.organizationID} onChange={set('organizationID')}>
          <option value="" disabled>select an organization</option>
          {organizations.map(o => <option key={o.organizationID} value={o.organizationID}>{o.organizationName}</option>)}
        </select></div>
      <div><label className="sw-label mb-1" htmlFor="rm-role">Role</label>
        <select id="rm-role" className="form-select" value={form.roleID} onChange={set('roleID')} disabled={!form.organizationID}>
          <option value="" disabled>{form.organizationID ? 'select a role' : 'select an organization first'}</option>
          {roles.items.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select></div>
      <div><label className="sw-label mb-1" htmlFor="rm-label">Label</label><input id="rm-label" className="form-control" value={form.label} onChange={set('label')} /></div>
      {error && <div className="text-danger small" role="alert">{error}</div>}
      {error && <ErrorReference error={failure} className="mt-0" />}
      <div className="d-flex gap-2 justify-content-end mt-auto">
        <button type="button" className="btn sw-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn sw-btn-primary" disabled={!ready || saving} onClick={handleAdd}>Add mapping</button>
      </div>
    </Drawer>
  )
}
